import { useState } from "react";
import SectionHeading from "./SectionHeading";
import { rankingTabs, rankingData } from "@/mocks/ranking";

type TabKey = keyof typeof rankingData;

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  flat: "ri-subtract-line",
};

const trendColor: Record<string, string> = {
  up: "text-primary-600",
  down: "text-foreground-400",
  flat: "text-secondary-600",
};

export default function Ranking() {
  const [activeTab, setActiveTab] = useState<TabKey>("popular");
  const list = rankingData[activeTab];
  const activeMeta = rankingTabs.find((t) => t.key === activeTab);

  return (
    <section id="ranking" className="w-full bg-background-100/70 px-4 py-16 md:px-8 md:py-24">
      <SectionHeading
        eyebrow="热门动漫排行"
        title="从人气、评分到期待值"
        description="三个维度分别回答不同问题：哪些作品在热播、哪些口碑扎实、哪些值得提前列入清单。"
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="inline-flex gap-1 rounded-full bg-background-200/70 p-1">
            {rankingTabs.map((tab) => {
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key as TabKey)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-background-50 text-primary-700"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <ol className="mt-5 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {list.map((row) => (
              <li
                key={`${activeTab}-${row.rank}`}
                className="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-background-100/70"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm ${
                    row.rank <= 3
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {row.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground-950">{row.title}</p>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">{row.genre}</p>
                </div>
                <span className={`hidden text-xs sm:inline-flex ${trendColor[row.trend]}`}>
                  <i className={trendIcon[row.trend]} />
                </span>
                <span className="flex w-16 shrink-0 flex-col items-end">
                  <span className="text-sm text-foreground-950">{row.value}</span>
                  <span className="text-[11px] text-foreground-500">{activeMeta?.valueLabel}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-lightbulb-flash-line text-lg" />
            </span>
            <h3 className="mt-4 text-lg text-foreground-950">怎么用榜单更快做决定</h3>
            <ul className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                先在人气榜与评分榜中找交集，交集越多通常越值得优先跟。
              </li>
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                期待榜用于提前排期，把下一阶段想看的作品先记下来。
              </li>
              <li className="flex gap-2.5">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                数值变化比单次排名更重要，连续上升通常意味着后劲足。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
            <h3 className="flex items-center gap-2 text-base text-secondary-900">
              <i className="ri-line-chart-line" />
              本期观察
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-secondary-900/80">
              治愈系与音乐题材在本期中段集体走高，慢热作品的完整播出更容易转化为稳定口碑。
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}